import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useParams } from "react-router-dom";

import type { OrgResponse } from "@bw/shared/types/generated";

import { PlatformPage } from "@/components/PlatformPage";
import { getContractTerms } from "@/shared/api";
import { orgBusinessName } from "@/shared/orgDisplay";

import {
  approveOrg,
  getNotes,
  getOrg,
  getOrgReconciliation,
  listOrgFacilities,
  reactivateOrg,
  rejectOrg,
  startReview,
  suspendOrg,
} from "../api";
import { OrgCommercialTermsPanel } from "../components/OrgCommercialTermsPanel";
import { OrgDetailHeader } from "../components/OrgDetailHeader";
import { OrgFacilitiesPanel } from "../components/OrgFacilitiesPanel";
import { OrgLaunchReadinessPanel } from "../components/OrgLaunchReadinessPanel";
import { OrgNotesPanel } from "../components/OrgNotesPanel";
import { OrgReconciliationSection } from "../components/OrgReconciliationSection";
import {
  OrgStatusActionsPanel,
  type ActiveOrgAction,
  type OrgActionError,
  type OrgActionVariables,
} from "../components/OrgStatusActionsPanel";

const ORG_ACTION_MUTATION_KEY = ["platform", "organization-action"] as const;

function readState(query: {
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  data: unknown;
}): "loading" | "error" | "ready" {
  if (query.isError || (query.isSuccess && query.data === undefined)) {
    return "error";
  }
  return query.isPending ? "loading" : "ready";
}

function actionErrorMessage(kind: OrgActionVariables["kind"]): string {
  switch (kind) {
    case "approve":
      return "Launch approval could not be completed. Review the blockers and try again.";
    case "start-review":
      return "Review could not be started. Try again.";
    case "reactivate":
      return "The organization could not be reactivated. Try again.";
    case "reject":
      return "The rejection could not be completed. Review the reason and try again.";
    case "suspend":
      return "The suspension could not be completed. Review the reason and try again.";
  }
}

function actionIsAllowed(
  current: OrgResponse | undefined,
  variables: OrgActionVariables,
): boolean {
  switch (variables.kind) {
    case "approve":
      return Boolean(
        current?.can_approve &&
          (current.status === "pending" || current.status === "in_review"),
      );
    case "reject":
      return current?.status === "in_review";
    case "start-review":
      return current?.status === "pending";
    case "suspend":
      return current?.status === "active";
    case "reactivate":
      return current?.status === "suspended";
  }
}

export function OrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const actionLock = useRef(false);
  const [activeAction, setActiveAction] = useState<ActiveOrgAction | null>(
    null,
  );
  const globalActionCount = useIsMutating({
    mutationKey: ORG_ACTION_MUTATION_KEY,
  });
  const actionPending = activeAction !== null || globalActionCount > 0;

  const orgQuery = useQuery({
    queryKey: ["platform", "org", id],
    queryFn: () => getOrg(id!),
    enabled: Boolean(id),
  });
  const facilitiesQuery = useQuery({
    queryKey: ["platform", "org-facilities", id],
    queryFn: () => listOrgFacilities(id!),
    enabled: Boolean(id),
  });
  const contractTermsQuery = useQuery({
    queryKey: ["platform", "contract-terms", id],
    queryFn: () => getContractTerms(id!),
    enabled: Boolean(id),
  });

  // Start every subordinate read independently even while the organization
  // shell is gated. The route injects these states into its panels so each API
  // is called once while retry controls remain local to the owning section.
  const notesQuery = useQuery({
    queryKey: ["platform", "org-notes", id],
    queryFn: () => getNotes(id!),
    enabled: Boolean(id),
  });
  const reconciliationQuery = useQuery({
    queryKey: ["platform", "org-reconciliation", id],
    queryFn: () => getOrgReconciliation(id!),
    enabled: Boolean(id),
  });

  const actionMutation = useMutation({
    mutationKey: ORG_ACTION_MUTATION_KEY,
    mutationFn: (variables: OrgActionVariables) => {
      const current = queryClient.getQueryData<OrgResponse>([
        "platform",
        "org",
        variables.orgId,
      ]);

      if (!actionIsAllowed(current, variables)) {
        throw new Error("Organization state changed");
      }

      switch (variables.kind) {
        case "approve":
          return approveOrg(variables.orgId);
        case "reject":
          return rejectOrg(variables.orgId, variables.reason);
        case "start-review":
          return startReview(variables.orgId);
        case "suspend":
          return suspendOrg(variables.orgId, variables.reason);
        case "reactivate":
          return reactivateOrg(variables.orgId);
      }
    },
    retry: false,
    onSuccess: async (_result, variables) => {
      const keys: unknown[][] = [
        ["platform", "org", variables.orgId],
        ["platform", "onboarding"],
        ["platform", "orgs"],
        ["platform", "dashboard-stats"],
        ["platform", "dashboard"],
        ["platform", "audit"],
      ];
      if (variables.kind === "reject") {
        keys.push(["platform", "org-notes", variables.orgId]);
      }
      await Promise.allSettled(
        keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    },
  });

  function acquireAction(variables: OrgActionVariables): boolean {
    if (
      actionLock.current ||
      queryClient.isMutating({ mutationKey: ORG_ACTION_MUTATION_KEY }) > 0
    ) {
      return false;
    }
    actionLock.current = true;
    setActiveAction({ kind: variables.kind, orgId: variables.orgId });
    return true;
  }

  function runAction(
    variables: OrgActionVariables,
    onSuccess?: () => void,
    onStale?: () => void,
  ) {
    const current = queryClient.getQueryData<OrgResponse>([
      "platform",
      "org",
      variables.orgId,
    ]);
    if (!actionIsAllowed(current, variables)) {
      onStale?.();
      return;
    }
    if (!acquireAction(variables)) return;
    actionMutation.mutate(variables, {
      onSuccess: (_result, settled) => {
        if (settled.orgId === id) onSuccess?.();
      },
      onSettled: () => {
        actionLock.current = false;
        setActiveAction(null);
      },
    });
  }

  const orgFailed =
    orgQuery.isError || (orgQuery.isSuccess && orgQuery.data === undefined);
  const org = orgQuery.data;
  const failedVariables = actionMutation.isError
    ? actionMutation.variables
    : undefined;
  const actionError: OrgActionError | null = failedVariables
    ? {
        orgId: failedVariables.orgId,
        kind: failedVariables.kind,
        message: actionErrorMessage(failedVariables.kind),
      }
    : null;

  return (
    <PlatformPage title={org ? orgBusinessName(org) : "Organization"}>
      {orgQuery.isPending ? (
        <Box
          role="status"
          aria-label="Loading organization"
          aria-live="polite"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress size={36} />
        </Box>
      ) : orgFailed || !org ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void orgQuery.refetch()}
            >
              Retry
            </Button>
          }
        >
          Organization details could not be loaded. Try again.
        </Alert>
      ) : (
        <Stack spacing={2.5}>
          <OrgDetailHeader org={org} />
          {(org.status === "pending" || org.status === "in_review") && (
            <OrgLaunchReadinessPanel org={org} />
          )}
          <OrgStatusActionsPanel
            key={`actions:${org.org_id}`}
            org={org}
            actionPending={actionPending}
            activeAction={activeAction}
            actionError={actionError}
            onAction={runAction}
          />
          <OrgReconciliationSection orgId={id!} read={reconciliationQuery} />
          <OrgCommercialTermsPanel
            orgId={id!}
            terms={contractTermsQuery.data}
            state={readState(contractTermsQuery)}
            onRetry={() => void contractTermsQuery.refetch()}
          />
          <OrgFacilitiesPanel
            facilities={facilitiesQuery.data}
            state={readState(facilitiesQuery)}
            onRetry={() => void facilitiesQuery.refetch()}
          />
          <OrgNotesPanel
            key={`notes:${org.org_id}`}
            orgId={org.org_id}
            read={notesQuery}
          />
        </Stack>
      )}
    </PlatformPage>
  );
}
